/*
 * CAPTURE-OWNED MODULE
 * Template Field Form Component for capture-ui
 * Modal for creating / editing a capture_template_field.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { getFieldTransforms, updateFieldTransforms } from '../api/captureTemplates';
import { deriveFieldKey } from './workflow/publishFieldSelection';
import FieldMatchSuggestions from './template-editor/FieldMatchSuggestions';
import { buildLibraryCatalog, findFieldSuggestions, needsSecondPress } from '../utils/libraryFieldSuggestions';

const TRANSFORM_TYPES = [
  { value: 'trim',             label: 'Trim whitespace' },
  { value: 'uppercase',        label: 'UPPERCASE' },
  { value: 'lowercase',        label: 'lowercase' },
  { value: 'title_case',       label: 'Title Case' },
  { value: 'sentence_case',    label: 'Sentence case' },
  { value: 'strip_prefix',     label: 'Strip prefix' },
  { value: 'strip_suffix',     label: 'Strip suffix' },
  { value: 'replace',          label: 'Find & replace' },
  { value: 'regex_capture',    label: 'Regex capture' },
  { value: 'normalize_number', label: 'Normalize number' },
  { value: 'normalize_date',   label: 'Normalize date' },
];

// ---- Date normalization (normalize_date transform) ----
// NOTE: keep in sync with normalizeDateValue() in
// capture-app/src/services/templateExtractor.ts (extraction source of truth).
// The two cannot share a module across the capture-app/capture-ui boundary.
const MONTH_INDEX = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
  may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8,
  sep: 9, sept: 9, september: 9, oct: 10, october: 10, nov: 11, november: 11,
  dec: 12, december: 12,
};
function pad2(n) { return String(n).padStart(2, '0'); }
function expandYear(y) { return y >= 100 ? y : (y >= 70 ? 1900 + y : 2000 + y); }
function isValidYMD(y, m, d) {
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}
function parseFlexibleDate(input) {
  const s = input.trim();
  if (!s) return null;
  // ISO-ish: YYYY-MM-DD or YYYY/MM/DD
  let mm = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (mm) { const y = +mm[1], m = +mm[2], d = +mm[3]; if (isValidYMD(y, m, d)) return { y, m, d }; }
  // Month-name: "January 7, 2024" / "Jan 7 2024" / "7 January 2024" / "7-Jan-2024"
  const monthMatch = s.toLowerCase().match(/\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t)?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\b/);
  if (monthMatch) {
    const m = MONTH_INDEX[monthMatch[1]];
    const nums = (s.match(/\d{1,4}/g) || []).map(Number);
    let y = nums.find(n => n >= 1000);
    const d = nums.find(n => n >= 1 && n <= 31 && n !== y);
    if (y == null) { const rest = nums.filter(n => n !== d); if (rest.length) y = expandYear(rest[rest.length - 1]); }
    if (m && d != null && y != null && isValidYMD(y, m, d)) return { y, m, d };
  }
  // Numeric: M/D/Y or M-D-Y (US month-first default)
  mm = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);
  if (mm) {
    const a = +mm[1], b = +mm[2], y = expandYear(+mm[3]);
    let m = a, d = b;
    if (a > 12 && b <= 12) { m = b; d = a; }            // clearly day-first
    if (isValidYMD(y, m, d)) return { y, m, d };
    if (isValidYMD(y, b, a)) return { y, m: b, d: a };  // last-resort swap
  }
  return null;
}
function formatDate(y, m, d, fmt) {
  switch (fmt) {
    case 'iso': return `${y}-${pad2(m)}-${pad2(d)}`;
    case 'eu':  return `${pad2(d)}/${pad2(m)}/${y}`;
    case 'us':
    default:    return `${pad2(m)}/${pad2(d)}/${y}`;
  }
}
function normalizeDateValue(input, config = {}) {
  const parsed = parseFlexibleDate(input);
  if (!parsed) return input.trim();                    // safe passthrough — never corrupt
  return formatDate(parsed.y, parsed.m, parsed.d, String(config?.format || 'us').toLowerCase());
}

// A key derived from the label, made unique against the template's other
// field keys by appending _2, _3 and so on. Empty when the label has no a-z or
// digit (deriveFieldKey is ASCII only).
export function uniqueDerivedKey(label, existingKeys) {
  const base = deriveFieldKey(label);
  if (!base) return '';
  const taken = new Set(existingKeys || []);
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

function applyTransformsPreview(value, transforms) {
  let result = value;
  for (const t of transforms) {
    switch (t.type) {
      case 'trim': result = result.trim(); break;
      case 'uppercase': result = result.toUpperCase(); break;
      case 'lowercase': result = result.toLowerCase(); break;
      case 'title_case': result = result.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()); break;
      case 'sentence_case': result = result.length > 0 ? result.charAt(0).toUpperCase() + result.slice(1).toLowerCase() : result; break;
      case 'strip_prefix': { const p = t.config?.prefix || ''; if (p && result.startsWith(p)) result = result.slice(p.length).trim(); break; }
      case 'strip_suffix': { const s = t.config?.suffix || ''; if (s && result.endsWith(s)) result = result.slice(0, -s.length).trim(); break; }
      case 'replace': { const from = t.config?.from || ''; const to = t.config?.to || ''; if (from) result = result.split(from).join(to); break; }
      case 'regex_capture': { const pat = t.config?.pattern || ''; if (pat) { try { const m = result.match(new RegExp(pat)); if (m?.[1]) result = m[1]; else if (m?.[0]) result = m[0]; } catch {} } break; }
      case 'normalize_number': { const c = result.replace(/[^0-9.\-]/g, ''); if (c) result = c; break; }
      case 'normalize_date': result = normalizeDateValue(result, t.config || {}); break;
      default: break;
    }
  }
  return result;
}

/**
 * Add mode only, all optional: libraryRows (GET /templates/field-library rows),
 * templateFields and documentType drive the similar-field suggestions;
 * onUseLibraryField(row) is called when the user picks a library field instead
 * of creating a custom one. Edit mode shows no suggestions.
 */
const TemplateFieldForm = ({
  field, onSave, onCancel, existingFieldKeys,
  libraryRows = null, templateFields = null, documentType = '', onUseLibraryField = null,
}) => {
  const isEdit = Boolean(field?.id);
  const templateId = field?.templateId || field?.template_id;
  const [fieldKey, setFieldKey] = useState(field?.field_key || '');
  // Add mode only: true once the user types in the key field, so label edits
  // stop overwriting it. Clearing the key field resets it.
  const [keyTouched, setKeyTouched] = useState(false);
  const [label, setLabel] = useState(field?.label || '');
  const [valueType, setValueType] = useState(field?.value_type || 'text');
  const [isRequired, setIsRequired] = useState(field?.required ?? false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Transforms state
  const [transforms, setTransforms] = useState([]);
  const [transformsLoading, setTransformsLoading] = useState(false);
  const [previewInput, setPreviewInput] = useState('');

  // Load existing transforms on mount for existing fields
  useEffect(() => {
    if (isEdit && templateId && field?.id) {
      setTransformsLoading(true);
      getFieldTransforms(templateId, field.id)
        .then(res => setTransforms((res.transforms || []).map(t => ({
          type: t.type,
          config: t.config || {},
        }))))
        .catch(err => console.error('[TemplateFieldForm] load transforms:', err))
        .finally(() => setTransformsLoading(false));
    }
  }, [isEdit, templateId, field?.id]);

  // Similar existing fields while typing (add mode only). Warn, never block.
  const catalog = useMemo(() => buildLibraryCatalog(libraryRows || []), [libraryRows]);
  const suggestions = useMemo(
    () => (isEdit ? null : findFieldSuggestions({ label, fieldKey, catalog, templateFields: templateFields || [], documentType })),
    [isEdit, label, fieldKey, catalog, templateFields, documentType]
  );
  // Soft second press: Save asks once more only for a same-template duplicate
  // or an exact standard-field match. Any edit to the name resets it.
  const [confirmPending, setConfirmPending] = useState(false);
  useEffect(() => { setConfirmPending(false); }, [label, fieldKey]);

  const previewOutput = useMemo(
    () => previewInput ? applyTransformsPreview(previewInput, transforms) : '',
    [previewInput, transforms]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitField({ force: confirmPending });
  };

  const submitField = async ({ force }) => {
    const key = fieldKey.trim().replace(/\s+/g, '_').toLowerCase();
    if (!key) {
      setError(isEdit
        ? 'Field key is required'
        : 'Field key is required. Enter a label with at least one letter or digit, or type a key.');
      return;
    }
    if (!isEdit && !force && needsSecondPress(suggestions)) {
      setConfirmPending(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const savedField = await onSave({
        id: field?.id || undefined,
        field_key: key,
        label: label.trim() || key,
        value_type: valueType,
        required: isRequired,
      });

      // Save transforms if we have a field ID and a templateId
      const fieldId = field?.id || savedField?.id;
      if (fieldId && templateId && transforms.length > 0) {
        try {
          await updateFieldTransforms(templateId, fieldId, transforms.map((t, i) => ({
            type: t.type,
            config: t.config || {},
            step_order: i,
          })));
        } catch (tErr) {
          console.error('[TemplateFieldForm] save transforms:', tErr);
        }
      } else if (fieldId && templateId && transforms.length === 0 && isEdit) {
        // Clear transforms if user removed all
        try {
          await updateFieldTransforms(templateId, fieldId, []);
        } catch (tErr) {
          console.error('[TemplateFieldForm] clear transforms:', tErr);
        }
      }
    } catch (err) {
      // The route's own text ("Field key "x" already exists in this template")
      // rather than axios's "Request failed with status code 409".
      setError(err?.response?.data?.error || err?.message || 'Failed to save field');
      setSubmitting(false);
    }
  };

  // Add mode: derive the key from the label until the user edits the key.
  // Edit mode never touches the key from here.
  const handleLabelChange = (val) => {
    setLabel(val);
    if (!isEdit && !keyTouched) {
      setFieldKey(uniqueDerivedKey(val, existingFieldKeys));
    }
  };

  const handleKeyChange = (val) => {
    setFieldKey(val);
    if (isEdit) return;
    if (val === '') {
      // Cleared: go back to deriving, starting from the current label.
      setKeyTouched(false);
      setFieldKey(uniqueDerivedKey(label, existingFieldKeys));
    } else {
      setKeyTouched(true);
    }
  };

  // Transform helpers
  const addTransform = () => {
    setTransforms(prev => [...prev, { type: 'trim', config: {} }]);
  };
  const removeTransform = (idx) => {
    setTransforms(prev => prev.filter((_, i) => i !== idx));
  };
  const updateTransformType = (idx, type) => {
    setTransforms(prev => prev.map((t, i) => i === idx ? { type, config: {} } : t));
  };
  const updateTransformConfig = (idx, key, value) => {
    setTransforms(prev => prev.map((t, i) =>
      i === idx ? { ...t, config: { ...t.config, [key]: value } } : t
    ));
  };
  const moveTransform = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= transforms.length) return;
    setTransforms(prev => {
      const arr = [...prev];
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return arr;
    });
  };

  const renderConfigInputs = (t, idx) => {
    switch (t.type) {
      case 'strip_prefix':
        return (
          <input type="text" placeholder="Prefix to strip" value={t.config?.prefix || ''}
            onChange={e => updateTransformConfig(idx, 'prefix', e.target.value)}
            style={{ flex: 1, minWidth: 120, padding: '3px 6px', fontSize: '0.82rem' }} />
        );
      case 'strip_suffix':
        return (
          <input type="text" placeholder="Suffix to strip" value={t.config?.suffix || ''}
            onChange={e => updateTransformConfig(idx, 'suffix', e.target.value)}
            style={{ flex: 1, minWidth: 120, padding: '3px 6px', fontSize: '0.82rem' }} />
        );
      case 'replace':
        return (
          <>
            <input type="text" placeholder="Find" value={t.config?.from || ''}
              onChange={e => updateTransformConfig(idx, 'from', e.target.value)}
              style={{ width: 90, padding: '3px 6px', fontSize: '0.82rem' }} />
            <input type="text" placeholder="Replace" value={t.config?.to || ''}
              onChange={e => updateTransformConfig(idx, 'to', e.target.value)}
              style={{ width: 90, padding: '3px 6px', fontSize: '0.82rem' }} />
          </>
        );
      case 'regex_capture':
        return (
          <input type="text" placeholder="Pattern (e.g. PO[- ]?(\d+))" value={t.config?.pattern || ''}
            onChange={e => updateTransformConfig(idx, 'pattern', e.target.value)}
            style={{ flex: 1, minWidth: 140, padding: '3px 6px', fontSize: '0.82rem', fontFamily: 'monospace' }} />
        );
      default:
        return null;
    }
  };

  return (
    <div className="template-field-form-modal">
      <div className="modal-backdrop" onClick={onCancel}></div>
      <div className="modal-content">
        <h3>{isEdit ? 'Edit Field' : 'Add Field'}</h3>
        <form onSubmit={handleSubmit}>
          {isEdit ? (
            <>
              <div className="form-group">
                <label>Field Key <span style={{ color: '#9ca3af', fontWeight: 400 }}>(e.g. invoice_number)</span></label>
                <input
                  type="text"
                  value={fieldKey}
                  onChange={(e) => setFieldKey(e.target.value)}
                  placeholder="invoice_number"
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Label <span style={{ color: '#9ca3af', fontWeight: 400 }}>(display name)</span></label>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => handleLabelChange(e.target.value)}
                  placeholder="Invoice Number"
                />
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="field-label">Label <span style={{ color: '#9ca3af', fontWeight: 400 }}>(display name)</span></label>
                <input
                  id="field-label"
                  type="text"
                  value={label}
                  onChange={(e) => handleLabelChange(e.target.value)}
                  placeholder="Invoice Number"
                  autoFocus
                />
                <FieldMatchSuggestions
                  suggestions={suggestions}
                  onUseLibraryField={onUseLibraryField}
                  onCreateAnyway={() => submitField({ force: true })}
                  confirmPending={confirmPending}
                  busy={submitting}
                  documentType={documentType}
                />
              </div>
              <div className="form-group">
                <label htmlFor="field-key">Field Key</label>
                <input
                  id="field-key"
                  type="text"
                  value={fieldKey}
                  onChange={(e) => handleKeyChange(e.target.value)}
                  placeholder="invoice_number"
                />
                <span className="field-key-hint" style={{ color: '#9ca3af', fontSize: '12px', marginTop: '2px' }}>
                  Made from the label. You normally do not need to change it.
                </span>
              </div>
            </>
          )}
          <div className="form-group">
            <label>Type</label>
            <select value={valueType} onChange={(e) => setValueType(e.target.value)}>
              <option value="text">Text</option>
              <option value="number">Number</option>
              <option value="date">Date</option>
              <option value="currency">Currency</option>
            </select>
          </div>
          <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="field-required"
              checked={isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
              style={{ width: 'auto', margin: 0 }}
            />
            <label htmlFor="field-required" style={{ margin: 0, fontWeight: 400 }}>Required field</label>
          </div>

          {/* ── Transforms Section ── */}
          <div className="ep-transforms-section" style={{ marginTop: 16, borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label style={{ fontWeight: 600, fontSize: '0.9rem', margin: 0 }}>Field Transforms</label>
              <button type="button" onClick={addTransform}
                style={{ fontSize: '0.78rem', padding: '2px 10px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' }}>
                + Add Transform
              </button>
            </div>

            {transformsLoading && <div style={{ fontSize: '0.8rem', color: '#9ca3af' }}>Loading transforms...</div>}

            {transforms.length === 0 && !transformsLoading && (
              <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginBottom: 8 }}>No transforms configured.</div>
            )}

            {transforms.map((t, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4,
                padding: '4px 6px', background: '#fafafa', borderRadius: 4, border: '1px solid #eee',
              }}>
                <span style={{ color: '#aaa', cursor: 'default', fontSize: '0.8rem', userSelect: 'none' }} title="Drag to reorder">≡</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <button type="button" onClick={() => moveTransform(idx, -1)} disabled={idx === 0}
                    style={{ fontSize: '0.6rem', lineHeight: 1, padding: 0, border: 'none', background: 'none', cursor: idx === 0 ? 'default' : 'pointer', color: idx === 0 ? '#ddd' : '#666' }}>▲</button>
                  <button type="button" onClick={() => moveTransform(idx, 1)} disabled={idx === transforms.length - 1}
                    style={{ fontSize: '0.6rem', lineHeight: 1, padding: 0, border: 'none', background: 'none', cursor: idx === transforms.length - 1 ? 'default' : 'pointer', color: idx === transforms.length - 1 ? '#ddd' : '#666' }}>▼</button>
                </div>
                <select value={t.type} onChange={e => updateTransformType(idx, e.target.value)}
                  style={{ fontSize: '0.82rem', padding: '3px 6px', minWidth: 130 }}>
                  {TRANSFORM_TYPES.map(tt => <option key={tt.value} value={tt.value}>{tt.label}</option>)}
                </select>
                {renderConfigInputs(t, idx)}
                <button type="button" onClick={() => removeTransform(idx)}
                  style={{ fontSize: '0.82rem', color: '#ef4444', border: 'none', background: 'none', cursor: 'pointer', padding: '2px 4px', fontWeight: 600 }}
                  title="Remove transform">✕</button>
              </div>
            ))}

            {/* Live Preview */}
            {transforms.length > 0 && (
              <div style={{ marginTop: 8, padding: '6px 8px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4 }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 500, color: '#64748b', marginBottom: 4 }}>Preview</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="text" placeholder="Sample value…" value={previewInput}
                    onChange={e => setPreviewInput(e.target.value)}
                    style={{ flex: 1, padding: '3px 6px', fontSize: '0.82rem', border: '1px solid #d1d5db', borderRadius: 3 }} />
                  <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>→</span>
                  <div style={{
                    flex: 1, padding: '3px 6px', fontSize: '0.82rem', fontFamily: 'monospace',
                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 3, minHeight: 22,
                    color: previewOutput !== previewInput ? '#059669' : '#374151',
                  }}>
                    {previewOutput || '\u00A0'}
                  </div>
                </div>
              </div>
            )}
          </div>

          {error && <p style={{ color: '#ef4444', fontSize: '13px', margin: '8px 0' }}>{error}</p>}
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : isEdit ? 'Update Field' : confirmPending ? 'Create anyway' : 'Add Field'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TemplateFieldForm;
